import { useCallback, useEffect, useState } from 'react';

import { clearViewHistory, listViewHistory, type DealViewRowJoined } from '@/lib/db/buyer';
import { formatAed } from '@/lib/format';

// ---------------------------------------------------------------------------
// UI view-history row
// ---------------------------------------------------------------------------

export type ViewHistoryRowUi = {
  id: string;
  dealTitle: string;
  zone: string;
  priceLabel: string;
  discountPct: number;
  dldConfidence: string | null;
  dldScope: string | null;
  // ISO YYYY-MM-DD slice of `viewed_at` — kept locale-agnostic V1; relative
  // time formatting is deferred to V2 (ADR-025 §H rationale: V1 only INSERTs
  // happen post-Phase C, so this column is rarely populated).
  viewedAt: string;
};

export function mapViewRowToUi(row: DealViewRowJoined): ViewHistoryRowUi {
  const deal = row.deals;
  return {
    id: row.id,
    dealTitle: deal?.title ?? '—',
    zone: deal?.zone ?? '—',
    priceLabel: deal?.asking_price ? formatAed(Number(deal.asking_price)) : '—',
    discountPct: deal?.discount_pct ?? 0,
    dldConfidence: deal?.dld_confidence ?? null,
    dldScope: deal?.dld_scope ?? null,
    viewedAt: row.viewed_at.slice(0, 10),
  };
}

// ---------------------------------------------------------------------------
// useViewHistory — read list, no insert hook (ADR-025 §H)
// ---------------------------------------------------------------------------

export type ViewHistoryState =
  | { status: 'loading'; history: null; error: null }
  | { status: 'success'; history: ViewHistoryRowUi[]; error: null }
  | { status: 'error'; history: null; error: Error };

const INITIAL_STATE: ViewHistoryState = { status: 'loading', history: null, error: null };

export function useViewHistory(): { state: ViewHistoryState; refresh: () => void } {
  const [state, setState] = useState<ViewHistoryState>(INITIAL_STATE);
  const [tick, setTick] = useState(0);

  const refresh = useCallback(() => {
    setTick((t) => t + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setState(INITIAL_STATE);

    listViewHistory()
      .then((rows) => {
        if (cancelled) return;
        setState({
          status: 'success',
          history: rows.map(mapViewRowToUi),
          error: null,
        });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setState({
          status: 'error',
          history: null,
          error: err instanceof Error ? err : new Error(String(err)),
        });
      });

    return () => {
      cancelled = true;
    };
  }, [tick]);

  return { state, refresh };
}

// ---------------------------------------------------------------------------
// useClearViewHistory — DELETE all rows for the current user
// ---------------------------------------------------------------------------

export type ClearViewHistoryStatus = 'idle' | 'clearing' | 'error';

export function useClearViewHistory(userId: string | null): {
  status: ClearViewHistoryStatus;
  clear: () => Promise<boolean>;
} {
  const [status, setStatus] = useState<ClearViewHistoryStatus>('idle');

  const clear = useCallback(async (): Promise<boolean> => {
    if (!userId) return false;
    setStatus('clearing');
    try {
      await clearViewHistory(userId);
      setStatus('idle');
      return true;
    } catch (err) {
      setStatus('error');
      throw err instanceof Error ? err : new Error(String(err));
    }
  }, [userId]);

  return { status, clear };
}
